/**
 * EmptyStateIllustration - Minimalist empty state visuals for Stock Sentry
 *
 * Design Philosophy: "Ethereal Commerce"
 * - Clean geometry meets soft gradients
 * - RetailForge brand purple as primary accent (#8b7bd8)
 * - Purposeful emptiness signals potential, not absence
 *
 * SVG assets extracted to public/illustrations/ for better maintainability.
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.3
 */

export type EmptyStateVariant =
  | 'default'
  | 'dashboard-header'
  | 'no-products'
  | 'no-alerts'
  | 'no-active-alerts'
  | 'no-monitored-products'
  | 'no-alert-history'
  | 'no-notifications'
  | 'no-emails'
  | 'no-channels'
  | 'weekly-digest'
  | 'notification-emails'
  | 'email-recipients'
  | 'slack'
  | 'webhooks'
  | 'data-export';

interface EmptyStateIllustrationProps {
  /** The type of empty state to display */
  variant?: EmptyStateVariant;
  /** Width of the illustration (default: 200) */
  width?: number;
  /** Height of the illustration (default: 160) */
  height?: number;
  /** Optional className for additional styling */
  className?: string;
  /** Margin for centering the illustration */
  margin?: string;
}

/**
 * Maps variant names to their asset paths
 */
const ILLUSTRATION_PATHS: Record<EmptyStateVariant, string> = {
  'default': '/illustrations/default.svg',
  'dashboard-header': '/illustrations/dashboard-header.svg',
  'no-products': '/illustrations/no-products.svg',
  'no-alerts': '/illustrations/no-alerts.svg',
  'no-active-alerts': '/illustrations/no-active-alerts.svg',
  'no-monitored-products': '/illustrations/no-monitored-products.svg',
  'no-alert-history': '/illustrations/no-alert-history.svg',
  'no-notifications': '/illustrations/no-notifications.svg',
  'no-emails': '/illustrations/no-emails.svg',
  'no-channels': '/illustrations/no-channels.svg',
  'weekly-digest': '/illustrations/weekly-digest.svg',
  'notification-emails': '/illustrations/notification-emails.svg',
  'email-recipients': '/illustrations/email-recipients.svg',
  'slack': '/images/empty-states/slack-empty-state.png',
  'webhooks': '/images/empty-states/webhook-empty-state.png',
  'data-export': '/illustrations/data-export.svg',
};

export function EmptyStateIllustration({
  variant = 'default',
  width = 200,
  height = 160,
  margin = '0 auto',
  className,
}: EmptyStateIllustrationProps) {
  const src = ILLUSTRATION_PATHS[variant];

  return (
    <div
      className="empty-state-image-wrapper"
      style={{ width, height, margin }}
    >
      <img
        src={src}
        alt={`Empty state illustration: ${variant}`}
        width={width}
        height={height}
        className={`empty-state-image ${className || ''}`}
        style={{ display: 'block', objectFit: 'contain' }}
      />
    </div>
  );
}

export default EmptyStateIllustration;
